import { useTranslation } from "react-i18next";
import { Link } from "~/components/ui/link";
import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { ProductWordmark } from "~/components/products/ProductWordmark";
import { cn } from "~/lib/utils";
import { imageUrl } from "~/lib/image-url";

interface FleetProduct {
    productCode: string;
    href: string;
    image: string;
    nameKey: string;
    descriptionKey: string;
    // Each product shot has different built-in margins, so each sets its own fit to land at the
    // design's rendered size in the 193x134 frame.
    imageClassName: string;
}

// Plain public/ paths so Picture can serve the AVIF/WebP siblings.
const PRODUCTS: FleetProduct[] = [
    {
        productCode: "bouncieobd",
        href: "/fleet/products/drive",
        image: imageUrl("/images/products/nav-drive-fleet.png"),
        nameKey: "subnav.products.drive.name",
        descriptionKey: "subnav.products.drive.description",
        // The PNG carries a wide soft shadow (the box is ~75% of the file), so it is placed by
        // its box: 113x114 at 39px/9px inside the frame, as in the design.
        imageClassName: "absolute top-[-6px] left-[16px] h-[149px] w-auto max-w-none",
    },
    {
        productCode: "bounciedashcam",
        href: "/products/view",
        image: imageUrl("/images/products/nav-dashcam-fleet.png"),
        nameKey: "subnav.products.view.name",
        descriptionKey: "subnav.products.view.description",
        imageClassName: "size-full object-contain px-5",
    },
    {
        productCode: "bounciecharger",
        href: "/products/go",
        image: imageUrl("/images/products/nav-go-fleet.png"),
        nameKey: "subnav.products.go.name",
        descriptionKey: "subnav.products.go.description_short",
        // The charger runs past the frame's bottom edge, as in the design.
        imageClassName: "absolute top-0 left-1/2 h-[122%] w-auto max-w-none -translate-x-1/2",
    },
];

function FleetProductCard({ product }: { product: FleetProduct }) {
    const { t } = useTranslation("fleet");
    const { t: tCommon } = useTranslation("common");
    const name = tCommon(product.nameKey);

    return (
        <li className="flex w-[193px] shrink-0 flex-col">
            <div className="bg-bg-alt border-border relative h-[134px] overflow-hidden rounded-t-2xl border border-b-0">
                {/* Decorative: the wordmark below already names the product. */}
                <Picture src={product.image} alt="" loading="lazy" className={product.imageClassName} />
            </div>
            {/* 15px bottom padding absorbs the 1px border, keeping the card at the design's height. */}
            <div className="bg-background border-border flex flex-1 flex-col items-center gap-3 rounded-b-2xl border border-t-0 px-4 pt-4 pb-[15px]">
                <div className="flex flex-col items-center gap-2 text-center">
                    <ProductWordmark productCode={product.productCode} name={name} className="text-text-heading h-[18px]" />
                    <p className="text-text-body-tertiary text-sm leading-5">{tCommon(product.descriptionKey)}</p>
                </div>
                <Button
                    asChild
                    variant="tertiary"
                    size="sm"
                    className="bg-bg-brand-subtle mt-auto h-11 w-full text-base leading-5 tracking-[0.16px]"
                >
                    <Link
                        data-sid={`fleet-product-cta:${product.href}`}
                        to={product.href}
                        aria-label={t("products.learn_more_aria", { name })}
                    >
                        {t("products.learn_more")}
                    </Link>
                </Button>
            </div>
        </li>
    );
}

// Must render inside the .fleet token scope (the /fleet route's wrapper).
export function FleetProducts() {
    const { t } = useTranslation("fleet");

    return (
        <section aria-labelledby="fleet-products-title" className="py-10 lg:py-12">
            <div className="wrap flex flex-col items-center gap-8">
                <h2
                    id="fleet-products-title"
                    className="font-display text-text-heading text-center text-xl leading-[1.15] font-extrabold lg:text-4xl lg:leading-[1.1]"
                >
                    {t("products.title")}
                </h2>
                {/* Scrolls sideways edge to edge on narrow screens; centered from md, where the row fits. */}
                <ul
                    className={cn(
                        "wrap-bleed flex gap-4 self-stretch overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
                        "md:justify-center lg:gap-6 lg:overflow-visible",
                    )}
                >
                    {PRODUCTS.map((product) => (
                        <FleetProductCard key={product.productCode} product={product} />
                    ))}
                </ul>
            </div>
        </section>
    );
}

export default FleetProducts;
